import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import dayjs from 'dayjs';

const STORAGE_KEY = 'varexe_dashboard_date_range_v1';

/**
 * Single canonical list for dashboard reporting presets (shown only inside the RangePicker panel).
 */
export const dashboardQuickRangeOptions = [
  { id: 'today', label: 'Today' },
  { id: 'last_7_days', label: 'Last 7 days' },
  { id: 'last_30_days', label: 'Last 30 days' },
  { id: 'last_4_weeks', label: 'Last 4 weeks' },
  { id: 'last_90_days', label: 'Last 90 days' },
  { id: 'last_6_months', label: 'Last 6 months' },
  { id: 'last_12_months', label: 'Last 12 months' },
  { id: 'month_to_date', label: 'Month to date' },
  { id: 'quarter_to_date', label: 'Quarter to date' },
  { id: 'year_to_date', label: 'Year to date' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'this_calendar_year', label: 'This calendar year' },
  { id: 'all_time', label: 'All time' }
];

export function defaultDashboardDateRange() {
  const end = dayjs();
  const start = end.subtract(29, 'day');
  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD')
  };
}

export function defaultDashboardState() {
  return {
    ...defaultDashboardDateRange(),
    rangePresetId: 'last_30_days',
    cashFlowBuckets: 'week',
    salesOverviewBuckets: 'week'
  };
}

function normalizeBucket(val) {
  return val === 'day' || val === 'week' || val === 'month' ? val : null;
}

/**
 * Dates for preset (inclusive).
 * @param {string} presetId
 */
export function computeQuickRangeDatesForPreset(presetId, now = dayjs()) {
  const today = now.startOf('day');

  switch (presetId) {
    case 'today':
      return { startDate: today.format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'last_7_days':
      return { startDate: today.subtract(6, 'day').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'last_30_days':
      return { startDate: today.subtract(29, 'day').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'last_4_weeks':
      return { startDate: today.subtract(27, 'day').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'last_90_days':
      return { startDate: today.subtract(89, 'day').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'last_6_months':
      return { startDate: today.subtract(6, 'month').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'last_12_months':
      return { startDate: today.subtract(12, 'month').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'month_to_date':
      return { startDate: today.startOf('month').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'quarter_to_date': {
      const qStartMonth = Math.floor(today.month() / 3) * 3;
      const qStart = dayjs(new Date(today.year(), qStartMonth, 1)).startOf('day');
      return { startDate: qStart.format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    }
    case 'year_to_date':
      return { startDate: today.startOf('year').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'this_month':
      return {
        startDate: today.startOf('month').format('YYYY-MM-DD'),
        endDate: today.endOf('month').format('YYYY-MM-DD')
      };
    case 'last_month': {
      const lm = today.subtract(1, 'month');
      return {
        startDate: lm.startOf('month').format('YYYY-MM-DD'),
        endDate: lm.endOf('month').format('YYYY-MM-DD')
      };
    }
    case 'this_calendar_year':
      return {
        startDate: today.startOf('year').format('YYYY-MM-DD'),
        endDate: today.endOf('year').format('YYYY-MM-DD')
      };
    case 'all_time':
      return { startDate: '2000-01-01', endDate: today.format('YYYY-MM-DD') };
    default:
      return defaultDashboardDateRange();
  }
}

export function matchingQuickRangePresetId(startDate, endDate, now = dayjs()) {
  const s = dayjs(startDate).startOf('day');
  const e = dayjs(endDate).startOf('day');
  for (const { id } of dashboardQuickRangeOptions) {
    const { startDate: cs, endDate: ce } = computeQuickRangeDatesForPreset(id, now);
    const csd = dayjs(cs).startOf('day');
    const ced = dayjs(ce).startOf('day');
    if (s.isSame(csd, 'day') && e.isSame(ced, 'day')) {
      return id;
    }
  }
  return null;
}

export function presetLabel(id) {
  const row = dashboardQuickRangeOptions.find((o) => o.id === id);
  return row ? row.label : null;
}

function loadStoredState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o.startDate !== 'string' || typeof o.endDate !== 'string') {
      return null;
    }

    const cashFlowBuckets = normalizeBucket(o.cashFlowBuckets) ?? 'week';
    const salesOverviewBuckets = normalizeBucket(o.salesOverviewBuckets) ?? 'week';

    let rangePresetId =
      typeof o.rangePresetId === 'string'
        ? o.rangePresetId
        : matchingQuickRangePresetId(o.startDate, o.endDate) ?? 'custom';

    const knownIds = new Set(dashboardQuickRangeOptions.map((x) => x.id));
    if (!knownIds.has(rangePresetId)) {
      rangePresetId = matchingQuickRangePresetId(o.startDate, o.endDate) ?? 'custom';
    }

    return {
      startDate: o.startDate,
      endDate: o.endDate,
      rangePresetId,
      cashFlowBuckets,
      salesOverviewBuckets
    };
  } catch {
    return null;
  }
}

const DashboardDateRangeContext = createContext(null);

export function DashboardDateRangeProvider({ children }) {
  const [dashboardState, setDashboardState] = useState(
    () => loadStoredState() ?? defaultDashboardState()
  );

  const mergeAndPersist = useCallback((partial) => {
    setDashboardState((prev) => {
      const next = { ...prev, ...partial };
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const setRange = useCallback(
    ({ startDate, endDate }) => {
      mergeAndPersist({
        startDate,
        endDate,
        rangePresetId: matchingQuickRangePresetId(startDate, endDate) ?? 'custom'
      });
    },
    [mergeAndPersist]
  );

  const applyReportingPreset = useCallback(
    (presetId) => {
      const dates = computeQuickRangeDatesForPreset(presetId);
      mergeAndPersist({
        ...dates,
        rangePresetId: presetId
      });
    },
    [mergeAndPersist]
  );

  const setCashFlowBuckets = useCallback(
    (bucket) => {
      const v = normalizeBucket(bucket);
      if (!v) return;
      mergeAndPersist({ cashFlowBuckets: v });
    },
    [mergeAndPersist]
  );

  const setSalesOverviewBuckets = useCallback(
    (bucket) => {
      const v = normalizeBucket(bucket);
      if (!v) return;
      mergeAndPersist({ salesOverviewBuckets: v });
    },
    [mergeAndPersist]
  );

  const value = useMemo(
    () => ({
      startDate: dashboardState.startDate,
      endDate: dashboardState.endDate,
      rangePresetId: dashboardState.rangePresetId,
      cashFlowBuckets: dashboardState.cashFlowBuckets,
      salesOverviewBuckets: dashboardState.salesOverviewBuckets,
      setRange,
      applyReportingPreset,
      setCashFlowBuckets,
      setSalesOverviewBuckets
    }),
    [
      dashboardState.startDate,
      dashboardState.endDate,
      dashboardState.rangePresetId,
      dashboardState.cashFlowBuckets,
      dashboardState.salesOverviewBuckets,
      setRange,
      applyReportingPreset,
      setCashFlowBuckets,
      setSalesOverviewBuckets
    ]
  );

  return (
    <DashboardDateRangeContext.Provider value={value}>
      {children}
    </DashboardDateRangeContext.Provider>
  );
}

export function useDashboardDateRange() {
  const v = useContext(DashboardDateRangeContext);
  if (!v) {
    throw new Error('useDashboardDateRange must be used within DashboardDateRangeProvider');
  }
  return v;
}

/** Inclusive calendar days between startDate and endDate (YYYY-MM-DD). */
export function inclusiveDayCount(startDate, endDate) {
  return dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
}

/** Grouping heuristic for invoice/sales timeline charts */
export function inferSalesChartGroupBy(startDate, endDate) {
  const days = inclusiveDayCount(startDate, endDate);
  if (days <= 21) return 'day';
  if (days <= 120) return 'week';
  if (days <= 800) return 'month';
  return 'year';
}

/** Grouping heuristic for bills + sales merged cash-flow chart */
export function inferCashFlowChartGroupBy(startDate, endDate) {
  const days = inclusiveDayCount(startDate, endDate);
  if (days <= 21) return 'day';
  if (days <= 98) return 'week';
  return 'month';
}

/** Human-readable headline for toolbar (preset name, Last N days, or explicit dates). */
export function describeDashboardReportingPeriod(startDate, endDate) {
  const presetId = matchingQuickRangePresetId(startDate, endDate);
  const fromList = presetId ? presetLabel(presetId) : null;
  if (fromList) return fromList;

  const s = dayjs(startDate).startOf('day');
  const e = dayjs(endDate).startOf('day');
  const today = dayjs().startOf('day');

  const monthStart = today.startOf('month');
  const monthEnd = today.endOf('month').startOf('day');
  if (s.isSame(monthStart, 'day') && e.isSame(monthEnd, 'day')) {
    return 'This month';
  }

  const prevStart = today.subtract(1, 'month').startOf('month');
  const prevEnd = today.subtract(1, 'month').endOf('month').startOf('day');
  if (s.isSame(prevStart, 'day') && e.isSame(prevEnd, 'day')) {
    return 'Last month';
  }

  const yearStart = today.startOf('year');
  const yearEnd = today.endOf('year').startOf('day');
  if (s.isSame(yearStart, 'day') && e.isSame(yearEnd, 'day')) {
    return 'This calendar year';
  }

  const last30Start = today.subtract(29, 'day');
  if (e.isSame(today, 'day') && s.isSame(last30Start, 'day')) {
    return 'Last 30 days';
  }

  const last90Start = today.subtract(89, 'day');
  if (e.isSame(today, 'day') && s.isSame(last90Start, 'day')) {
    return 'Last 90 days';
  }

  const days = inclusiveDayCount(startDate, endDate);
  if (e.isSame(today, 'day') && days >= 2) {
    return `Last ${days} days`;
  }

  return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
}
